import { Fragment, useEffect, useRef, useState, type FormEvent, type JSX } from 'react';
import type { ChatMessage } from '../api.ts';
import { dayLabel } from '../lib/people.ts';

/**
 * The conversation window, used by both sides.
 *
 * One component rather than two, because the difference between a teacher's view and a pupil's is
 * which bubble sits on the right — a single boolean, not a second screen to keep in step with
 * this one.
 */

/**
 * The clock under a bubble.
 *
 * Only a clock: every bubble now sits under a heading that already names its day, and repeating
 * "12 мар" on each of forty messages says nothing forty times.
 */
function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

export function Chat({
  messages,
  mineAreFromStudent,
  onSend,
  placeholder,
  disabled,
}: {
  messages: ChatMessage[];
  /** True in the pupil's cabinet: their own messages are the ones marked from_student. */
  mineAreFromStudent: boolean;
  onSend: (body: string) => Promise<void>;
  placeholder: string;
  disabled?: boolean;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  // A conversation is read from the bottom: the newest line is the one you came for.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  async function send(): Promise<void> {
    const body = draft.trim();
    if (!body || sending) return;

    setSending(true);
    try {
      await onSend(body);
      setDraft('');
    } finally {
      setSending(false);
    }
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    void send();
  }

  return (
    <div className="chat">
      <div className="chat__log">
        {messages.length === 0 ? (
          <p className="muted chat__empty">Здесь пока пусто. Напишите первое сообщение.</p>
        ) : null}

        {messages.map((message, index) => {
          const mine = message.from_student === mineAreFromStudent;

          // A heading whenever the day changes, so a thread that ran over a fortnight does not
          // read as one long afternoon.
          const previous = messages[index - 1];
          const opensADay =
            !previous || dayLabel(previous.created_at) !== dayLabel(message.created_at);

          return (
            <Fragment key={message.id}>
              {opensADay ? (
                <div className="chat__day">
                  <span>{dayLabel(message.created_at)}</span>
                </div>
              ) : null}

              <div className={`bubble${mine ? ' bubble--mine' : ''}`}>
                {!mine ? <div className="bubble__who">{message.sender_name}</div> : null}
                <div className="bubble__body">{message.body}</div>
                <div className="bubble__time">
                  {formatTime(message.created_at)}
                  {/*
                   * Ticks on your own messages only, because they answer a question only the
                   * sender has: one for written down, two for read. Without them a teacher who
                   * asks something and hears nothing cannot tell silence from not-yet-seen, and
                   * writes again — or worse, waits.
                   */}
                  {mine ? (
                    <span
                      className={`ticks${message.read_by_other ? ' ticks--read' : ''}`}
                      title={message.read_by_other ? 'Прочитано' : 'Доставлено'}
                      aria-label={message.read_by_other ? 'Прочитано' : 'Доставлено'}
                    >
                      {message.read_by_other ? '✓✓' : '✓'}
                    </span>
                  ) : null}
                </div>
              </div>
            </Fragment>
          );
        })}

        <div ref={endRef} />
      </div>

      <form className="chat__form" onSubmit={submit}>
        <textarea
          className="input chat__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          rows={2}
          maxLength={4000}
          disabled={disabled}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter starts a new line — what everyone already expects from a
            // chat box, and what stops a half-typed question going out mid-thought.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <button className="btn" type="submit" disabled={disabled ?? (sending || !draft.trim())}>
          {sending ? '…' : 'Отправить'}
        </button>
      </form>
    </div>
  );
}
